import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  OnInit,
  Inject,
  PLATFORM_ID,
  ChangeDetectorRef,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LanguageRoutingService } from '../../services/language-routing.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { InputComponent } from '../../components/input/input.component';
import { SelectComponent } from '../../components/select/select.component';
import { MultiSelectComponent } from '../../components/multi-select/multi-select.component';
import { CardComponent } from '../card/card.component';
import { SafeUrl } from '@angular/platform-browser';
import { UserAvatarComponent } from '../user-avatar/user-avatar.component';
import { ButtonComponent } from '../../components/button/button.component';
import { AlertCardComponent } from '../../components/alert-card/alert-card.component';
import { InputCheckboxComponent } from '../input-checkbox/input-checkbox.component';
import { EnvironmentService } from '../../../environments/environment-loader';
import { getJobPositions } from '../../../../../../libs/shared/src/lib/i18n/job-positions/index';
import { getJobTypes } from '../../../../../../libs/shared/src/lib/i18n/job-types/index';
import { getSelfDescriptions } from '../../../../../../libs/shared/src/lib/i18n/self-descriptions/index';
import { PlacesAutocompleteComponent } from '../../../../../../libs/shared/src/lib/google/places-autocomplete/places-autocomplete.component';
import { Address } from '../../../../../../libs/shared/src/lib/models/address.model';
import { ConsentType } from '../../../../../../libs/shared/src/lib/models/consent.model';
import type { CountryCode } from 'libphonenumber-js';
import {
  DEFAULT_PHONE_COUNTRY,
  formatPhoneForInput,
  getPhoneCountryOptions,
  PhoneCountryOption,
  sanitizePhoneInputValue,
} from '../../utils/phone-number.utils';

@Component({
  selector: 'app-profile-personal-info-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    TranslocoModule,
    InputComponent,
    InputCheckboxComponent,
    SelectComponent,
    MultiSelectComponent,
    CardComponent,
    ButtonComponent,
    AlertCardComponent,
    PlacesAutocompleteComponent,
    UserAvatarComponent,
  ],
  templateUrl: './profile-personal-info-form.component.html',
  styleUrls: ['./profile-personal-info-form.component.scss'],
})
export class ProfilePersonalInfoFormComponent implements OnInit, OnChanges {
  @Input() personalInfoForm!: FormGroup;
  @Input() error: string | null = null;
  @Input() disabled = false;
  @Input() userEmail: string = '';
  @Input() profileSettings: any = null;
  @Input() isAuthenticated: boolean = false; // Track if user is logged in
  @Input() missingFields: string[] = []; // List of missing required fields
  @Input() educationLevelOptions: { value: number; label: string }[] = [];
  @Input() consentUpdatingTypes: Set<ConsentType> = new Set<ConsentType>();
  // Avatar (rendered at the top of the Essentials card; editable in all modes)
  @Input() avatarPhoto: SafeUrl | string | null = null;
  @Input() avatarFirstName = '';
  @Input() avatarLastName = '';
  @Input() avatarIdentifier = '';
  @Input() avatarLoading = false;
  @Output() avatarEdit = new EventEmitter<void>();
  @Output() avatarRemove = new EventEmitter<void>();
  @Output() submitEvent = new EventEmitter<any>();
  @Output() cancelEvent = new EventEmitter<any>();
  @Output() addressChange = new EventEmitter<Address>();

  // Access all input and select components to manually mark them as touched
  @ViewChildren(InputComponent) inputComponents!: QueryList<InputComponent>;
  @ViewChildren(SelectComponent) selectComponents!: QueryList<SelectComponent>;

  // Store selected address from Google Places
  selectedAddress: Address | null = null;

  // Track whether to show manual address fields
  showManualAddressFields = false;

  // Track if form has been submitted (to show all validation errors)
  formSubmitted = false;

  // Options for multi-select fields - dynamically loaded from shared translations
  jobTypeOptions: { value: string; label: string }[] = [];
  selfDescriptionOptions: { value: string; label: string }[] = [];
  jobCategoryOptions: { value: string; label: string }[] = [];
  jobPositionOptions: { value: string; label: string }[] = [];
  phoneCountryOptions: PhoneCountryOption[] = [];
  readonly ConsentType = ConsentType;

  // Store all positions with their category mappings for filtering
  allPositions: { id: number; positionKey: string; jobCategoryId: number }[] = [];

  // Store category key to ID mapping
  categoryKeyToIdMap: Map<string, number> = new Map();

  get settingsLink(): string {
    return this.languageRoutingService.getLocalizedRoute('/settings');
  }

  isConsentUpdating(consentType: ConsentType): boolean {
    return this.consentUpdatingTypes.has(consentType);
  }

  constructor(
    private readonly http: HttpClient,
    private readonly translocoService: TranslocoService,
    public languageRoutingService: LanguageRoutingService,
    private readonly cdr: ChangeDetectorRef,
    private readonly environmentService: EnvironmentService,
    private router: Router,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {}

  ngOnInit(): void {
    this.loadPhoneCountryOptions();

    if (isPlatformBrowser(this.platformId)) {
      this.loadAllOptions();
    }

    // Check if address fields are pre-filled (user editing existing profile)
    this.checkIfAddressFieldsAreFilled();

    // Subscribe to language changes and reload options when language changes
    this.translocoService.langChanges$.subscribe((lang) => {
      this.loadPhoneCountryOptions(lang);

      if (isPlatformBrowser(this.platformId)) {
        this.loadAllOptions();
      }
    });
  }

  loadAllOptions(): void {
    // Only load profile settings (job preferences) for authenticated users
    if (this.isAuthenticated) {
      this.loadProfileSettings();
    }
    this.loadSelfDescriptions();
  }

  loadProfileSettings(): void {
    // If profileSettings are passed from parent, use them
    if (this.profileSettings) {
      this.processProfileSettings(this.profileSettings);
      return;
    }

    // Fallback: load from API if not provided (backward compatibility)
    interface JobEmploymentType {
      id: number;
      employmentTypeKey: string;
      sortOrder?: number;
      active?: boolean;
    }

    interface JobCategory {
      id: number;
      categoryKey: string;
    }

    interface JobPosition {
      id: number;
      positionKey: string;
      jobCategoryId: number;
    }

    interface ProfileSettings {
      categories: JobCategory[];
      employmentTypes: JobEmploymentType[];
      userStatuses: string[];
      positionsOfActiveCategories?: JobPosition[];
      positions?: JobPosition[]; // All available positions with their category mappings
    }

    // Create headers with required x-client-type
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-client-type': 'candidate',
    });

    this.http.get<ProfileSettings>(`${this.environmentService.environment.openApiUrl}api/get-profile-settings`, { headers }).subscribe({
      next: (settings) => {
        this.processProfileSettings(settings);
      },
      error: (error: any) => {
        console.error('Failed to load profile settings:', error);
        // Fallback to hardcoded values if API fails
        const currentLang = this.translocoService.getActiveLang() as 'sk' | 'en' | 'cz';
        const jobTypes = getJobTypes(currentLang);
        this.jobTypeOptions = Object.entries(jobTypes.jobTypes).map(([value, label]) => ({
          value,
          label,
        }));
      },
    });
  }

  /**
   * Process profile settings data (from parent or API)
   */
  private processProfileSettings(settings: any): void {
    // Process employment types
    const currentLang = this.translocoService.getActiveLang() as 'sk' | 'en' | 'cz';
    const jobTypesData = getJobTypes(currentLang);

    this.jobTypeOptions = settings.employmentTypes.map((et: any) => ({
      value: et.employmentTypeKey,
      label: (jobTypesData.jobTypes as any)[et.employmentTypeKey] || et.employmentTypeKey,
    }));

    // Process job categories and build key-to-ID mapping
    const jobPositions = getJobPositions(currentLang);

    this.jobCategoryOptions = settings.categories.map((cat: any) => {
      // Build mapping from categoryKey to categoryId
      this.categoryKeyToIdMap.set(cat.categoryKey, cat.id);
      return {
        value: cat.categoryKey,
        label: (jobPositions.jobCategories as any)[cat.categoryKey] || cat.categoryKey,
      };
    });

    // Store all positions with their category mappings
    if (settings.positions && Array.isArray(settings.positions)) {
      this.allPositions = settings.positions;
    }

    // Filter positions based on currently selected categories (for initial load)
    this.updateJobPositionOptions();

    // Set up listener for category changes
    this.setupCategoryChangeListener();
  }

  loadSelfDescriptions(): void {
    const currentLang = this.translocoService.getActiveLang() as 'sk' | 'en' | 'cz';
    const selfDescriptions = getSelfDescriptions(currentLang);

    this.selfDescriptionOptions = Object.entries(selfDescriptions.selfDescriptions).map(([value, label]) => ({
      value,
      label,
    }));
  }

  /**
   * Set up listener for job category changes to dynamically filter positions
   */
  private setupCategoryChangeListener(): void {
    if (!this.personalInfoForm) {
      return;
    }

    const jobCategoryControl = this.personalInfoForm.get('jobCategory');
    if (jobCategoryControl) {
      jobCategoryControl.valueChanges.subscribe((selectedCategories: string[]) => {
        this.updateJobPositionOptions();
        this.cleanupInvalidPositionSelections(selectedCategories);
      });
    }
  }

  /**
   * Update job position options based on selected categories
   */
  private updateJobPositionOptions(): void {
    if (!this.personalInfoForm) {
      return;
    }

    const selectedCategoryKeys = this.personalInfoForm.get('jobCategory')?.value || [];

    if (!Array.isArray(selectedCategoryKeys) || selectedCategoryKeys.length === 0) {
      // No categories selected - show no positions
      this.jobPositionOptions = [];
      return;
    }

    // Convert selected category keys to category IDs using our mapping
    const selectedCategoryIds = selectedCategoryKeys.map((key) => this.categoryKeyToIdMap.get(key)).filter((id) => id !== undefined) as number[];

    // Filter positions that belong to the selected categories
    const filteredPositions = this.allPositions.filter((pos) => selectedCategoryIds.includes(pos.jobCategoryId));

    // Translate and map positions
    const currentLang = this.translocoService.getActiveLang() as 'sk' | 'en' | 'cz';
    const jobPositions = getJobPositions(currentLang);

    this.jobPositionOptions = filteredPositions.map((pos) => ({
      value: pos.positionKey,
      label: (jobPositions.jobPositions as any)[pos.positionKey] || pos.positionKey,
    }));
  }

  /**
   * Remove selected positions that are no longer valid (not in selected categories)
   */
  private cleanupInvalidPositionSelections(selectedCategories: string[]): void {
    if (!this.personalInfoForm) {
      return;
    }

    const jobPositionControl = this.personalInfoForm.get('jobPosition');
    if (!jobPositionControl) {
      return;
    }

    const selectedPositions = jobPositionControl.value || [];
    if (!Array.isArray(selectedPositions) || selectedPositions.length === 0) {
      return;
    }

    // Get valid position keys based on current category selection
    const validPositionKeys = this.jobPositionOptions.map((opt) => opt.value);

    // Filter out invalid positions
    const validSelectedPositions = selectedPositions.filter((posKey) => validPositionKeys.includes(posKey));

    // Update the form control if selections changed
    if (validSelectedPositions.length !== selectedPositions.length) {
      jobPositionControl.setValue(validSelectedPositions);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled'] && this.personalInfoForm) {
      if (this.disabled) {
        this.personalInfoForm.disable();
      } else {
        this.personalInfoForm.enable();
      }
    }

    // If personalInfoForm exists and we don't have options yet, load them
    if (changes['personalInfoForm'] && this.personalInfoForm && this.jobCategoryOptions.length === 0 && this.isAuthenticated) {
      this.loadProfileSettings();
    }

    // Check if address fields are pre-filled when form changes
    if (changes['personalInfoForm'] && this.personalInfoForm) {
      this.checkIfAddressFieldsAreFilled();
    }
  }

  /**
   * Check if the form is valid
   */
  isValid(): boolean {
    return this.personalInfoForm?.valid || false;
  }

  /**
   * Mark form as touched to show validation errors
   */
  markFormAsTouched(): void {
    // Use built-in markAllAsTouched which recursively marks all controls
    this.personalInfoForm.markAllAsTouched();

    // Also mark all input and select components' internal touched state
    this.inputComponents?.forEach((input: InputComponent) => input.markAsTouched());
    this.selectComponents?.forEach((select: SelectComponent) => select.markAsTouched());

    // Trigger change detection to update the view
    this.cdr.detectChanges();
  }

  /**
   * Get the form value
   */
  getFormValue(): any {
    return this.personalInfoForm?.value || {};
  }

  onPhoneCountryChange(country: string | number): void {
    this.formatPhoneInput(country as CountryCode);
  }

  onPhoneInputChange(value: string): void {
    this.formatPhoneInput(this.selectedPhoneCountry, value);
  }

  formatPhoneInput(country: CountryCode = this.selectedPhoneCountry, valueOverride?: string): void {
    const phoneControl = this.personalInfoForm?.get('phone');
    const value = sanitizePhoneInputValue(valueOverride ?? phoneControl?.value);
    if (!phoneControl) return;

    const formattedPhone = value ? formatPhoneForInput(value, country) : '';
    if (formattedPhone !== value) {
      phoneControl.setValue(formattedPhone, { emitEvent: false });
    }
  }

  cancel(): void {
    // Reset form submission state and touched state
    this.formSubmitted = false;
    this.personalInfoForm.markAsUntouched();
    this.personalInfoForm.markAsPristine();

    // Reset all individual controls to untouched
    Object.keys(this.personalInfoForm.controls).forEach((key) => {
      const control = this.personalInfoForm.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });

    this.cancelEvent.emit();
  }

  submit(): void {
    // Mark form as submitted to show all validation errors
    this.formSubmitted = true;

    if (!this.isValid()) {
      // If address fields have validation errors, show them so user can see what's missing
      const hasAddressErrors =
        this.personalInfoForm.get('streetName')?.invalid ||
        this.personalInfoForm.get('streetNumber')?.invalid ||
        this.personalInfoForm.get('city')?.invalid ||
        this.personalInfoForm.get('zipCode')?.invalid ||
        this.personalInfoForm.get('country')?.invalid;

      if (hasAddressErrors && !this.showManualAddressFields) {
        this.showManualAddressFields = true;
        // Trigger change detection to render address fields
        this.cdr.detectChanges();
      }

      // Mark all fields as touched AFTER showing hidden fields
      this.markFormAsTouched();

      return;
    }
    this.submitEvent.emit();
  }

  onAddressSelected(address: Address): void {
    // Ignore null/undefined - don't wipe out existing address data
    if (!address) {
      return;
    }

    // Store the full address object (including lat/lng from Google Places)
    this.selectedAddress = address;

    // Emit to parent so it can store the address with lat/lng
    this.addressChange.emit(address);

    // Populate form fields from Google Places address
    if (address.streetName) {
      this.personalInfoForm.patchValue({ streetName: address.streetName });
    }
    if (address.streetNumber) {
      this.personalInfoForm.patchValue({ streetNumber: address.streetNumber });
    }
    if (address.city) {
      this.personalInfoForm.patchValue({ city: address.city });
    }
    if (address.postalCode) {
      this.personalInfoForm.patchValue({ zipCode: address.postalCode });
    }
    if (address.country) {
      this.personalInfoForm.patchValue({ country: address.country });
    }
  }

  /**
   * Allow user to manually fill address fields without using Google Places
   */
  showManualFields(): void {
    this.showManualAddressFields = true;
  }

  /**
   * Check if any address fields are pre-filled (for editing existing profiles)
   */
  private checkIfAddressFieldsAreFilled(): void {
    if (!this.personalInfoForm) {
      return;
    }

    const streetName = this.personalInfoForm.get('streetName')?.value;
    const streetNumber = this.personalInfoForm.get('streetNumber')?.value;
    const city = this.personalInfoForm.get('city')?.value;
    const zipCode = this.personalInfoForm.get('zipCode')?.value;
    const country = this.personalInfoForm.get('country')?.value;

    // If any address field has a value, show the manual fields
    if (streetName || streetNumber || city || zipCode || country) {
      this.showManualAddressFields = true;
    }
  }

  /**
   * Get the selected address
   */
  getSelectedAddress(): Address | null {
    return this.selectedAddress;
  }

  private loadPhoneCountryOptions(lang: string = this.translocoService.getActiveLang()): void {
    this.phoneCountryOptions = getPhoneCountryOptions(lang);
  }

  private get selectedPhoneCountry(): CountryCode {
    return (this.personalInfoForm?.get('phoneCountry')?.value || DEFAULT_PHONE_COUNTRY) as CountryCode;
  }
}
